import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Box, CircularProgress } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { getFullImageUrl, getProductPrimaryImageUrl } from '../../utils/imageUtils';
import { productService } from '../../services/productService';

// Cache for fetched products to avoid repeated API calls
const productCache = new Map();

// Cache for regenerated presigned URLs
const presignedUrlCache = new Map();

/**
 * Extracts the object path from a MinIO URL
 * @param {string} url - The MinIO URL
 * @returns {string|null} - The object path (e.g., "2026/01/09/filename.jpg")
 */
const extractMinIOObjectPath = (url) => {
  if (!url) return null;
  
  // Remove query parameters
  let baseUrl = url.split('?')[0];
  
  // Extract path after bucket name (uploads/)
  // Format: http://127.0.0.1:9000/uploads/path/to/file.jpg
  const match = baseUrl.match(/\/uploads\/(.+)$/);
  if (match) {
    return match[1];
  }
  
  // If it's already a path without the domain, return as is
  if (baseUrl.startsWith('uploads/') || baseUrl.startsWith('/uploads/')) {
    return baseUrl.replace(/^\/?uploads\//, '');
  }
  
  return null;
};

const ProductImage = ({ 
  item, 
  product, 
  width = 40, 
  height = 40, 
  variant = "rounded",
  sx = {},
  showLoading = true 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fetchedProduct, setFetchedProduct] = useState(null);

  // Determine the image source with proper fallback chain
  // Use useMemo to recalculate when dependencies change
  const imageSrc = useMemo(() => {
    // For order items, try productImageUrl first (most common in order items)
    if (item?.productImageUrl && typeof item.productImageUrl === 'string' && item.productImageUrl.trim() !== '') {
      const url = getFullImageUrl(item.productImageUrl);
      if (url) return url;
    }
    
    // Try productImage field (alternative field name)
    if (item?.productImage && typeof item.productImage === 'string' && item.productImage.trim() !== '') {
      const url = getFullImageUrl(item.productImage);
      if (url) return url;
    }
    
    // Try product object passed as prop
    if (product) {
      const url = getProductPrimaryImageUrl(product, false, 'thumbnail');
      if (url) return url;
    }

    // Try fetched product (from useEffect)
    if (fetchedProduct) {
      const url = getProductPrimaryImageUrl(fetchedProduct, false, 'thumbnail');
      if (url) return url;
    }

    // Try item.product (nested product object)
    if (item?.product) {
      const url = getProductPrimaryImageUrl(item.product, false, 'thumbnail');
      if (url) return url;
    }
    
    return null;
  }, [item?.productImageUrl, item?.productImage, product, fetchedProduct, item?.product]);

  // Fetch product details if we have productId but no image
  useEffect(() => {
    const fetchProductIfNeeded = async () => {
      // Check if we already have an image from props (without fetchedProduct)
      const hasImageFromProps = 
        (item?.productImageUrl && typeof item.productImageUrl === 'string' && item.productImageUrl.trim() !== '' && getFullImageUrl(item.productImageUrl)) ||
        (item?.productImage && typeof item.productImage === 'string' && item.productImage.trim() !== '' && getFullImageUrl(item.productImage)) ||
        (product && getProductPrimaryImageUrl(product, false, 'thumbnail')) ||
        (item?.product && getProductPrimaryImageUrl(item.product, false, 'thumbnail'));
      
      // Only fetch if we don't have an image and we have productId
      // Fetch even if product/item.product exists but doesn't have an image
      if (!hasImageFromProps && item?.productId) {
        // Don't fetch if we already have fetchedProduct for this productId
        if (fetchedProduct && (fetchedProduct.id === item.productId || fetchedProduct.product?.id === item.productId)) {
          return;
        }
        
        // Don't fetch if product prop has the same ID and we've already checked it
        if (product && (product.id === item.productId || product.product?.id === item.productId)) {
          return;
        }
        
        // Don't fetch if item.product has the same ID
        if (item?.product && (item.product.id === item.productId || item.product.product?.id === item.productId)) {
          return;
        }
        
        // Check cache first
        if (productCache.has(item.productId)) {
          const cachedProduct = productCache.get(item.productId);
          setFetchedProduct(cachedProduct);
          // Reset loading if we got a cached product with image
          if (getProductPrimaryImageUrl(cachedProduct, false, 'thumbnail')) {
            setLoading(true); // Will be set to false when image loads
          }
          return;
        }

        try {
          const fetched = await productService.getProductById(item.productId);
          // Handle both direct product and wrapped product responses
          // API typically returns { product: {...} } format
          const productData = fetched.product || fetched;
          if (productData) {
            productCache.set(item.productId, productData);
            setFetchedProduct(productData);
            // Reset loading if we got a product with image
            const imageUrl = getProductPrimaryImageUrl(productData, false, 'thumbnail');
            if (imageUrl) {
              setLoading(true); // Will be set to false when image loads
            }
          }
        } catch (err) {
          // Silently fail - will show placeholder
          console.debug(`[ProductImage] Could not fetch product ${item.productId}:`, err.message);
        }
      }
    };

    fetchProductIfNeeded();
  }, [item?.productId, item?.productImageUrl, item?.productImage, product, item?.product, fetchedProduct]);

  // Reset loading when imageSrc changes
  useEffect(() => {
    if (imageSrc) {
      setLoading(true);
      setError(false);
    } else {
      setLoading(false);
    }
  }, [imageSrc]);

  const handleImageLoad = () => {
    setLoading(false);
    setError(false);
  };

  const [regeneratingUrl, setRegeneratingUrl] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleImageError = async (event) => {
    setLoading(false);
    
    // Limit retries to avoid infinite loops
    if (retryCount >= 2) {
      setError(true);
      return;
    }
    
    // Check if it's a 403 Forbidden (expired presigned URL) or other error
    const img = event.target;
    const currentSrc = img.src;
    
    // If it's a MinIO URL that failed, try to regenerate presigned URL via backend
    if (currentSrc && (currentSrc.includes('127.0.0.1:9000') || currentSrc.includes('localhost:9000') || currentSrc.includes('X-Amz-'))) {
      if (!regeneratingUrl) {
        setRegeneratingUrl(true);
        try {
          // Extract object path from MinIO URL
          const objectPath = extractMinIOObjectPath(currentSrc);
          if (objectPath) {
            // Check cache first
            if (presignedUrlCache.has(objectPath)) {
              const cachedUrl = presignedUrlCache.get(objectPath);
              img.src = cachedUrl;
              setError(false);
              setLoading(true);
              setRetryCount(prev => prev + 1);
              setRegeneratingUrl(false);
              return;
            }
            
            // Construct the MinIO URL format that backend expects
            const minioBaseUrl = currentSrc.includes('127.0.0.1') ? 'http://127.0.0.1:9000' : 'http://localhost:9000';
            const fullMinioUrl = `${minioBaseUrl}/uploads/${objectPath}`;
            
            // Get fresh presigned URL from backend
            const encodedUrl = encodeURIComponent(fullMinioUrl);
            const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
            const response = await fetch(`${apiBaseUrl}/files/presigned-url?url=${encodedUrl}`);
            if (response.ok) {
              const data = await response.json();
              if (data.success && data.url) {
                // Cache the new presigned URL
                presignedUrlCache.set(objectPath, data.url);
                // Update the image source with fresh presigned URL
                img.src = data.url;
                setError(false);
                setLoading(true);
                setRetryCount(prev => prev + 1);
                setRegeneratingUrl(false);
                return;
              }
            }
          }
        } catch (err) {
          console.debug(`[ProductImage] Failed to regenerate presigned URL:`, err.message);
        }
        setRegeneratingUrl(false);
      }
    }
    
    setError(true);
    
    // If image failed to load and we have productId but haven't fetched product yet, try fetching
    if (item?.productId && !fetchedProduct && !product && !item?.product) {
      // Check cache first
      if (productCache.has(item.productId)) {
        const cachedProduct = productCache.get(item.productId);
        setFetchedProduct(cachedProduct);
        setError(false);
        setLoading(true);
        return;
      }
      
      // Fetch product as fallback
      productService.getProductById(item.productId)
        .then(fetched => {
          const productData = fetched.product || fetched;
          if (productData) {
            productCache.set(item.productId, productData);
            setFetchedProduct(productData);
            setError(false);
            setLoading(true);
          }
        })
        .catch(err => {
          console.debug(`[ProductImage] Fallback fetch failed for ${item.productId}:`, err.message);
        });
    }
  };

  // If no image source is available, show mos-logo.jpg in grayscale
  if (!imageSrc || error) {
    return (
      <Avatar
        src="/mos-logo.jpg"
        sx={{ 
          width, 
          height, 
          bgcolor: 'grey.100',
          color: 'grey.500',
          filter: 'grayscale(100%)',
          opacity: 0.3,
          ...sx 
        }}
        variant={variant}
        imgProps={{
          style: {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          }
        }}
      >
        <ImageIcon />
      </Avatar>
    );
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {loading && showLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width,
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: variant === 'rounded' ? 1 : '50%',
            zIndex: 1,
          }}
        >
          <CircularProgress size={Math.min(width, height) * 0.4} />
        </Box>
      )}
      <Avatar
        src={imageSrc}
        sx={{ 
          width, 
          height,
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          ...sx 
        }}
        variant={variant}
        onLoad={handleImageLoad}
        onError={handleImageError}
        imgProps={{
          style: {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
          }
        }}
      >
        <ImageIcon />
      </Avatar>
    </Box>
  );
};

export default ProductImage;